import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import identityApi from '../clients/IdentityClient';
import { useAuth } from '../context/AuthContext';
import LogoLink from './LogoLink';

const ChangeEmail: React.FC = () => {
  const { changeId } = useParams<{ changeId: string }>();
  const navigate = useNavigate();
  const { refreshUserProfile } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRedirectTimer = () => {
    if (redirectTimer.current) {
      clearTimeout(redirectTimer.current);
      redirectTimer.current = null;
    }
  };

  useEffect(() => {
    const confirmEmailChange = async () => {
      setStatus('loading');
      setMessage('');

      if (!changeId) {
        setStatus('error');
        setMessage('Invalid email change link');
        return;
      }

      try {
        const response = await identityApi.confirmEmailChange(changeId);

        if (!response.ok) {
          const errorMessage = await response.text();
          setStatus('error');
          setMessage(errorMessage || 'Email change verification failed. Please request a new email change.');
          return;
        }

        const hasAuthToken = Boolean(localStorage.getItem('authToken'));

        if (hasAuthToken) {
          try {
            await refreshUserProfile();
          } catch (error) {
            localStorage.removeItem('driverUser');
          }
        }

        setStatus('success');
        setMessage('Your email address has been updated successfully.');

        clearRedirectTimer();
        redirectTimer.current = setTimeout(() => {
          navigate(hasAuthToken ? '/dashboard/settings' : '/login');
        }, 3000);
      } catch (error) {
        setStatus('error');
        setMessage('Email change verification failed. Please try again or request a new email change.');
      }
    };

    confirmEmailChange();

    return () => {
      clearRedirectTimer();
    };
  }, []);

  const getAlertVariant = () => {
    if (status === 'success') return 'success';
    if (status === 'error') return 'danger';
    return 'info';
  };

  const hasAuthToken = Boolean(localStorage.getItem('authToken'));

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="card">
          <div className="text-center mb-4">
            <LogoLink className="navbar-brand" imgClassName="me-2" />
            <h3 className="text-accent m-2 heading">Confirm Email Change</h3>
          </div>

          <div className={`alert alert-${getAlertVariant()} text-center`} role="status">
            {status === 'loading' ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Verifying your new email address...
              </>
            ) : (
              <span>{message}</span>
            )}
          </div>

          {status === 'success' && (
            <div className="text-center mt-3">
              <small className="text-paragraphs d-block mb-2">
                Redirecting in 3 seconds.
              </small>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate(hasAuthToken ? '/dashboard' : '/login')}
              >
                {hasAuthToken ? 'Return to dashboard' : 'Return to login'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeEmail;